import { AutocompleteProps } from "@material-ui/lab";
import dayjs from "dayjs";
import * as React from "react";

import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { RegistrationContext } from "@/components/property_registration/RegistrationContext";
import {
  CustomForm as RegistrationForm,
  CustomSelector as RegistrationSelector,
  RegistrationAutocomplete as SharedAutocomplete,
  DatePickerComponent as RegistrationDatePicker,
} from "@/components/property_registration/RegistrationInputs";
import { FormRow } from "@/components/selection/FormRow";
import { WheelPicker } from "@/components/selection/WheelPicker";

export { CustomFormLabel } from "@/components/property_registration/RegistrationInputs";

// 履歴の月入力は保存処理が YYYY-MM の文字列を受け取る。表示だけ年月ピッカーにする。
export const CustomForm = React.forwardRef<HTMLInputElement, React.ComponentProps<typeof RegistrationForm>>(
  (props, ref) => {
    const v2 = React.useContext(RegistrationContext);
    const root = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(
      v2 ? ref : undefined,
      () => ({ focus: () => root.current?.querySelector<HTMLButtonElement>("button")?.focus() } as HTMLInputElement)
    );
    if (!v2) return <RegistrationForm {...props} ref={ref} />;
    const optional = ["priceof_facilities", "property_acquisition_tax", "misc_expences_at_bought"].includes(props.name)
      ? true
      : props.optional;
    if (props.type !== "month")
      return (
        <div ref={root} data-edit-field={props.name}>
          <RegistrationForm {...props} optional={optional} />
        </div>
      );
    if (props.disabled)
      return (
        <div ref={root} data-edit-field={props.name}>
          <FormRow
            label={props.label}
            onClick={() => undefined}
            value={props.value ? dayjs(String(props.value)).format("YYYY年M月") : undefined}
            disabled
          />
        </div>
      );
    return (
      <div ref={root} data-edit-field={props.name}>
        <RegistrationDatePicker
          label={typeof props.label === "string" ? props.label : undefined}
          name={props.name}
          value={props.value ? dayjs(String(props.value)).toDate() : undefined}
          required={props.required}
          error={props.error}
          errorText={typeof props.errorText === "string" ? props.errorText : undefined}
          onBlur={() =>
            props.onBlur?.({ target: { name: props.name, value: props.value } } as React.FocusEvent<HTMLInputElement>)
          }
          onChange={(value) =>
            props.onChange({
              type: "change",
              target: { name: props.name, value: dayjs(value).format("YYYY-MM") },
            } as React.ChangeEvent<HTMLInputElement>)
          }
        />
      </div>
    );
  }
);
CustomForm.displayName = "PropertyEditForm";

// 購入日は既存の年月日を維持する。年月だけを変えて1日に上書きしない。
export const DatePickerComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof RegistrationDatePicker> & { disabled?: boolean }
>((props, ref) => {
  const v2 = React.useContext(RegistrationContext);
  const root = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(
    v2 ? ref : undefined,
    () => ({ focus: () => root.current?.querySelector<HTMLButtonElement>("button")?.focus() } as HTMLInputElement)
  );
  const [open, setOpen] = React.useState(false);
  const [year, setYear] = React.useState(dayjs().year());
  const [month, setMonth] = React.useState(dayjs().month() + 1);
  const [day, setDay] = React.useState(1);
  if (!v2) return <RegistrationDatePicker {...props} ref={ref} />;
  if (props.name !== "bought_at")
    return (
      <div ref={root}>
        <RegistrationDatePicker
          {...props}
          optional={props.name === "property_acquisition_tax_payment_month" ? true : props.optional}
        />
      </div>
    );
  const date = props.value ? dayjs(props.value) : null;
  const days = dayjs()
    .date(1)
    .year(year)
    .month(month - 1)
    .daysInMonth();
  const selectedDay = Math.min(day, days);
  const close = () => {
    setOpen(false);
    props.onBlur?.();
  };
  const start = () => {
    if (props.disabled) return;
    setYear(date?.isValid() ? date.year() : dayjs().year());
    setMonth(date?.isValid() ? date.month() + 1 : dayjs().month() + 1);
    setDay(date?.isValid() ? date.date() : 1);
    setOpen(true);
  };
  const label = props.label || "購入年月日";
  return (
    <>
      <div ref={root} data-registration-field={props.name}>
        <FormRow
          label={label}
          value={date?.isValid() ? date.format("YYYY年M月D日") : undefined}
          disabled={props.disabled}
          error={props.error ? props.errorText : undefined}
          errorSurface
          onClick={start}
        />
      </div>
      <BottomSheet
        open={open}
        title={label}
        onOpen={start}
        onClose={close}
        footer={
          <CustomButton
            type="button"
            buttonSize="L"
            onClick={() => {
              props.onChange(
                dayjs()
                  .date(1)
                  .year(year)
                  .month(month - 1)
                  .date(selectedDay)
                  .startOf("day")
                  .toDate()
              );
              close();
            }}
          >
            決定
          </CustomButton>
        }
      >
        <WheelPicker
          columns={[
            {
              label: "年",
              values: Array.from({ length: 201 }, (_, i) => 1900 + i),
              value: year,
              initialValue: year,
              unit: "年",
              onChange: setYear,
            },
            {
              label: "月",
              values: Array.from({ length: 12 }, (_, i) => i + 1),
              value: month,
              initialValue: month,
              unit: "月",
              onChange: setMonth,
            },
            {
              label: "日",
              values: Array.from({ length: days }, (_, i) => i + 1),
              value: selectedDay,
              initialValue: selectedDay,
              unit: "日",
              onChange: setDay,
            },
          ]}
          echo={`${year}年${month}月${selectedDay}日`}
        />
      </BottomSheet>
    </>
  );
});
DatePickerComponent.displayName = "PropertyEditDatePicker";

export const CustomSelector = React.forwardRef<HTMLSelectElement, React.ComponentProps<typeof RegistrationSelector>>(
  (props, ref) => {
    const v2 = React.useContext(RegistrationContext);
    const root = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(
      v2 ? ref : undefined,
      () => ({ focus: () => root.current?.querySelector<HTMLButtonElement>("button")?.focus() } as HTMLSelectElement)
    );
    if (!v2) return <RegistrationSelector {...props} ref={ref} />;
    return (
      <div ref={root}>
        <RegistrationSelector {...props} />
      </div>
    );
  }
);
CustomSelector.displayName = "PropertyEditSelector";

export const RegistrationAutocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>({
  registrationRef,
  registrationName,
  ...props
}: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> & {
  registrationLabel: string;
  registrationError?: string;
  registrationRequired?: boolean;
  registrationOnBlur?: () => void;
  registrationRef?: React.Ref<HTMLDivElement>;
  registrationName?: string;
}) => {
  const v2 = React.useContext(RegistrationContext);
  const root = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(
    v2 ? registrationRef : undefined,
    () => ({ focus: () => root.current?.querySelector<HTMLButtonElement>("button")?.focus() } as HTMLDivElement)
  );
  if (!v2) return <SharedAutocomplete {...props} />;
  return (
    <div ref={root} data-registration-field={registrationName}>
      <SharedAutocomplete
        {...props}
        renderInput={(params) => {
          const input = props.renderInput(params);
          return React.isValidElement(input) ? React.cloneElement(input, { ref: null } as React.Attributes) : input;
        }}
      />
    </div>
  );
};
